import { reaction } from 'mobx';

import audioManager from '../managers/AudioManager';
import docReaderManager from '../managers/DocReaderManager';
import highlightAnnotationManager from '../managers/HighlightAnnotationManager';
import lessonManager from '../managers/LessonManager';
import navMenuManager from '../managers/NavMenuManager';
import questionFeedbackManager from '../managers/QuestionFeedbackManager';
import responseManager from '../managers/ResponseManager';
import scoringManager from '../managers/ScoringManager';
import studentActivityManager from '../managers/StudentActivityManager';
import timeManager from '../managers/TimeManager';
import toolbarManager from '../managers/ToolbarManager';
import userManager from '../managers/UserManager';
import videoManager from '../managers/VideoManager';

import { ContentType, FlyoutOpenState, FlyoutSide, FlyoutTabs, LessonMode, LessonSlideIds, ScoreState } from '../../Constants';

import alignmentService from './AlignmentService';
import highlightAnnotationService from './HighlightAnnotationService';
import questionService from './QuestionService';
import responseService from './ResponseService';
import scoringService from './ScoringService';
import studentActivityService from './StudentActivityService';
import toolbarService from './ToolbarService';
import htmlService from './HtmlService';
import playerService from './PlayerService';
import {
  getContentChildIdsForPdfPage,
  getFirstLessonElementIdOnPdfPage,
  getPageForActivityChild
} from './PdfActivityService';
import pdfActivityNavigationService from './PdfActivityNavigationService';

export class NavMenuService {
  cardWidth = 500;

  constructor() {
    reaction(
      () => ({
        readerPage: docReaderManager.currentPageNumber,
        slideId: lessonManager.currentLessonElementId,
        pendingNav: docReaderManager.sideNavPdfPage,
        pdfEntryViaActivityParent: docReaderManager.pdfEntryViaActivityParentSlide,
        lessonDirectedPdfTargetPage: docReaderManager.lessonDirectedPdfTargetPage,
        pdfLessonPendingReaderPage: docReaderManager.pdfLessonPendingReaderPage,
        pdfGotoOutsideActivityNavPageOne: docReaderManager.pdfGotoOutsideActivityNavPageOne,
      }),
      () => this.applyPdfActivityReaderSlideSync()
    );
  }

  // do not remove saveLock without a code review and a reason.
  // this prevents too many server hits and  race conditions
  // between saving events
  saveLock = false;

  initActivityNavigatorLessonElementMap = ({ activityLessonElement } = {}) => {
    const activityNavigatorLessonElementMap = new Map();

    for (let index = 0; index < activityLessonElement?.lessonElementIds?.length; index++) {
      const childElementId = activityLessonElement.lessonElementIds[index];

      const childModel = lessonManager.getSlideModel(childElementId);
      const parentModel = lessonManager.getSlideModel(childModel?.parentActivityElementId);

      if (childModel.inGroup) {
        const groupList = childModel.inGroup ? activityLessonElement.lessonElementGroups.get(childModel.groupId) || [] : [];
        const isFirstInGroup = groupList.indexOf(childModel.lessonElementId) === 0;
        activityNavigatorLessonElementMap.set(childModel.lessonElementId, {
          childModel,
          parentModel,
          groupList,
          index,
          isFirstInGroup
        });
      } else {
        activityNavigatorLessonElementMap.set(childModel.lessonElementId, {
          childModel,
          parentModel,
          index
        });
      }
    }
    navMenuManager.setActivityNavigatorLessonElementMap(activityNavigatorLessonElementMap);
  }

  async previousSlideHandler(event) {
    try {
      docReaderManager.clearPdfGotoOutsideActivityNavPageOne();
      docReaderManager.clearSuppressScheduleAlignOnDocReaderPageloadFromFooterPrevious();
      lessonManager.setIgnoreQuestionChildEventScrollListener(true);
      setTimeout(async () => {
        lessonManager.setIgnoreQuestionChildEventScrollListener(false);
      }, 300);

      const array = navMenuManager.activityNavigatorLessonElementValues;
      const filteredArray = array.filter((item) => {
        if (lessonManager.getFilteredSlides().indexOf(item) > -1) {
          return true;
        }
        return false;
      });

      const isFirstIndex = (filteredArray.length <= 0) || lessonManager.currentLessonElementId === filteredArray[0];

      if (lessonManager.lessonOptions.navigateWithinActivity && (questionService.activityWithQuestions(lessonManager.currentLessonElementId) && !isFirstIndex)) {
        const lessonElementId = undefined;
        await this.navToSlideClickHandler(lessonElementId, {
          fromPreviousSlideHandler: true
        });
      } else {
        if (!this.saveLock) {
          this.saveLock = true;
          await this.doPreNavigationActions(false);
          if (!lessonManager.lessonOptions.navigateWithinActivity) {
            docReaderManager.setSuppressScheduleAlignOnDocReaderPageloadFromFooterPrevious(true);
            docReaderManager.setSuppressInteractiveFlyoutOpenAfterFooterPreviousSlide(true);
          }
          lessonManager.previousSlide();
          await this.doPostNavigationActions({ event, fromPreviousSlideHandler: true });
          this.saveLock = false;
        }
      }
    } catch (error) {
      this.saveLock = false;
      console.error(error);
    }
  }

  async nextSlideHandler(event) {
    try {
      docReaderManager.clearPdfIframeNavigationSuppressFlags();
      docReaderManager.clearPdfGotoOutsideActivityNavPageOne();
      docReaderManager.clearSuppressInteractiveFlyoutOpenAfterFooterPreviousSlide();
      lessonManager.setIgnoreQuestionChildEventScrollListener(true);
      setTimeout(async () => {
        lessonManager.setIgnoreQuestionChildEventScrollListener(false);
      }, 300);

      const array = navMenuManager.activityNavigatorLessonElementValues;
      const filteredArray = array.filter((item) => {
        if (lessonManager.getFilteredSlides().indexOf(item) > -1) {
          return true;
        }
        return false;
      });

      const isLastIndex = (filteredArray.length <= 0) || lessonManager.currentLessonElementId === filteredArray[filteredArray.length - 1];

      if (lessonManager.lessonOptions.navigateWithinActivity && questionService.activityWithQuestions(lessonManager.currentLessonElementId) && !isLastIndex) {
        const lessonElementId = undefined;
        await this.navToSlideClickHandler(lessonElementId, {
          fromNextSlideHandler: true,
        });
      } else {
        if (!this.saveLock) {
          this.saveLock = true;
          await this.doPreNavigationActions(false);
          const nextIndex = lessonManager.getTargetSlideIndexAfterNextSlide();
          if (nextIndex != null && lessonManager.involvesPdfActivitySlideAtIndex(nextIndex)) {
            docReaderManager.setSuppressPdfSlideLandingFromLessonNav({
              iframeSync: true,
              pageloadAlign: true,
            });
          }
          lessonManager.nextSlide();
          await this.doPostNavigationActions({ event, fromNextSlideHandler: true });
          this.clearPdfActivityPageOneEntryAfterNext(true);
          this.saveLock = false;
        }
      }
    } catch (error) {
      this.saveLock = false;
      console.error(error);
    }
  }

  async navToSlideClickHandler(lessonElementId, {
    event,
    fromNextSlideHandler = false,
    fromPreviousSlideHandler = false,
    fromCardHandler = false,
    fromGotoMenuCard = false,
    fromPdfActivityReaderFlyoutSync = false,
    shouldReturnIfSameLessonElementId = true,
    shouldScrollQuestionIntoView = true,
    sideNavVideoProgressValue,
    sideNavAudioProgressValue
  } = {}) {
    try {
      docReaderManager.clearPdfIframeNavigationSuppressFlags();
      if (!fromPdfActivityReaderFlyoutSync) {
        docReaderManager.clearPdfGotoOutsideActivityNavPageOne();
        docReaderManager.clearSuppressInteractiveFlyoutOpenAfterFooterPreviousSlide();
      }
      if (window.ReadSpeaker && window.ReadSpeaker.ui?.getActivePlayer?.()) {
        window.ReadSpeaker.ui.getActivePlayer().close();
        window.ReadSpeaker.ui.destroyActivePlayer();
      }

      let model, parentModel;

      if (fromNextSlideHandler || fromPreviousSlideHandler) {
        const oldIndex = navMenuManager.selectedActivityNavigatorLessonElementIndex;
        const newIndex = fromNextSlideHandler ? oldIndex + 1 : oldIndex - 1;

        if (!navMenuManager.activityNavigatorLessonElementValues[newIndex]) {
          return;
        }

        const {
          childModel: _childModel,
          parentModel: _parentModel,
        } = navMenuManager.activityNavigatorLessonElementValues[newIndex];

        model = _childModel;
        parentModel = _parentModel;

        lessonElementId = _childModel.lessonElementId;
      } else {
        model = lessonManager.getSlideModel(lessonElementId);
        parentModel = lessonManager.getSlideModel(model?.parentActivityElementId);
      }

      let pdfGotoPageOneNav = false;
      if (
        fromGotoMenuCard &&
        !fromNextSlideHandler &&
        !fromPreviousSlideHandler
      ) {
        const pdfParentModel =
          model?.type === ContentType.PDF_ACTIVITY.type
            ? model
            : (parentModel?.type === ContentType.PDF_ACTIVITY.type ? parentModel : null);
        if (pdfParentModel) {
          const activityLe = lessonManager.getLessonElement(pdfParentModel.lessonElementId);
          const orderedIds = activityLe?.lessonElementIds;
          const filteredSlides = lessonManager.getFilteredSlides();
          const firstOnPage1 = orderedIds?.length
            ? getFirstLessonElementIdOnPdfPage(
              pdfParentModel,
              1,
              orderedIds,
              (id) => lessonManager.getSlideModel(id),
              filteredSlides
            )
            : null;
          const firstInActivity = activityLe
            ? lessonManager.getParentFirstSlide(activityLe)
            : null;
          const resolvedId = firstOnPage1 || firstInActivity;
          if (resolvedId) {
            pdfGotoPageOneNav = true;
            docReaderManager.setPdfGotoOutsideActivityNavPageOne(true);
            lessonElementId = resolvedId;
            model = lessonManager.getSlideModel(lessonElementId);
            parentModel = lessonManager.getSlideModel(model?.parentActivityElementId);
          }
        }
      }

      const isSameLessonElementId = lessonElementId === lessonManager.currentLessonElementId;
      if (shouldReturnIfSameLessonElementId && isSameLessonElementId && !pdfGotoPageOneNav) {
        return;
      }

      /**
       * Summary / goto jump straight to a child without mounting ActivityNavigator first — the map can
       * still describe another activity, so `selectedActivityNavigatorLessonElementIndex` stays stale and
       * `applyPdfActivityReaderSlideSync` navigates to the wrong child (last visited).
       */
      if (model?.isActivityPart && parentModel && (parentModel.lessonElementId || model.parentActivityElementId)) {
        const parentActivityId = parentModel.lessonElementId ?? model.parentActivityElementId;
        const activityLessonElement = lessonManager.getLessonElement(parentActivityId);
        if (activityLessonElement?.lessonElementIds?.length) {
          if (parentActivityId !== navMenuManager.selectedParentActivityLessonElement?.id) {
            navMenuManager.setSelectedParentActivityLessonElement(activityLessonElement);
          }
          this.initActivityNavigatorLessonElementMap({ activityLessonElement });
        }
      }

      let selectedChildLessonElement;

      if (questionService.activityWithQuestions(lessonElementId)) {
        selectedChildLessonElement = navMenuManager.activityNavigatorLessonElementMap.get(lessonElementId);
        if (selectedChildLessonElement?.index >= 0) {
          navMenuManager.setSelectedActivityNavigatorLessonElementIndex(
            selectedChildLessonElement.index
          );
        }
      }

      if (parentModel?.type === ContentType.VIDEO_QUESTION.type) {
        this.updateVideoQuestionNavSettings({
          model, parentModel, sideNavVideoProgressValue
        });
      } else if (parentModel?.type === ContentType.AUDIO_QUESTION.type) {
        this.updateAudioQuestionNavSettings({
          model, parentModel, sideNavAudioProgressValue
        });
      }

      const slides = lessonManager.getFilteredSlides();
      let index = slides.indexOf(lessonElementId);
      const lessonElement = lessonManager.getLessonElement(lessonElementId);

      // if we are navigating to an activity and the first item isn't visible
      // find one that is.
      if (model?.isActivity && index < 0 && lessonElement?.lessonElementIds) {
        for (const childId of lessonElement.lessonElementIds) {
          index = slides.indexOf(childId);
          if (index > -1) {
            break;
          }
        }
      }

      const skipSave = (questionFeedbackManager.noSubmit) ? false : event?.type === 'scroll';

      if (videoManager.isVideoTranscriptDialogOpen) {
        videoManager.setIsVideoTranscriptDialogOpen(false);
      }

      const landsOnPdfActivity = index >= 0 && lessonManager.involvesPdfActivitySlideAtIndex(index);

      const canNavigate = !this.saveLock || fromPdfActivityReaderFlyoutSync;
      if (canNavigate && index >= 0) {
        const ownedLock = !this.saveLock && !fromPdfActivityReaderFlyoutSync;
        if (ownedLock) {
          this.saveLock = true;
        }
        try {
          await this.doPreNavigationActions(fromPdfActivityReaderFlyoutSync ? true : skipSave);
          if (landsOnPdfActivity && (fromGotoMenuCard || fromNextSlideHandler)) {
            docReaderManager.setSuppressPdfSlideLandingFromLessonNav({
              iframeSync: true,
              pageloadAlign: fromNextSlideHandler,
            });
          }
          lessonManager.setCurrentSlideIndex(index, false, { pdfGotoPageOne: pdfGotoPageOneNav });
          await this.doPostNavigationActions({
            event,
            shouldScrollQuestionIntoView: fromPdfActivityReaderFlyoutSync ? false : shouldScrollQuestionIntoView,
            fromNextSlideHandler,
            fromCardHandler,
            fromGotoMenuCard,
          });
          this.clearPdfActivityPageOneEntryAfterNext(fromNextSlideHandler);
        } finally {
          if (ownedLock) {
            this.saveLock = false;
          }
        }
      }
    } catch (error) {
      this.saveLock = false;
      console.error(error);
    }
  }

  updateVideoQuestionNavSettings = ({
    model,
    parentModel,
    sideNavVideoProgressValue
  } = {}) => {
    if (parentModel?.type !== ContentType.VIDEO_QUESTION.type) {
      return;
    }
    let updatedProgressValue;
    if (!isNaN(sideNavVideoProgressValue) && typeof sideNavVideoProgressValue === 'number') {
      updatedProgressValue = sideNavVideoProgressValue;
    } else {
      updatedProgressValue = model?.showTime;
    }
    if (updatedProgressValue !== videoManager.sideNavVideoProgressValue &&
      (parentModel.pauseAtChildShowTime || !videoManager.isVideoPlaying)
    ) {
      videoManager.setSideNavVideoProgressValue(updatedProgressValue);
    }
  }

  updateAudioQuestionNavSettings = ({
    model,
    parentModel,
    sideNavAudioProgressValue
  } = {}) => {
    if (parentModel?.type !== ContentType.AUDIO_QUESTION.type) {
      return;
    }
    let updatedProgressValue;
    if (!isNaN(sideNavAudioProgressValue) && typeof sideNavAudioProgressValue === 'number') {
      updatedProgressValue = sideNavAudioProgressValue;
    } else {
      updatedProgressValue = model?.showTime;
    }
    if (updatedProgressValue !== audioManager.sideNavAudioProgressValue &&
      (parentModel.pauseAtChildShowTime || !audioManager.isAudioPlaying)
    ) {
      audioManager.setSideNavAudioProgressValue(updatedProgressValue);
    }
  }

  /**
   * Footer / activity Next landing on PDF page 1 with no linked questions: clear parent-entry flag.
   */
  clearPdfActivityPageOneEntryAfterNext = (fromNextSlideHandler) => {
    if (!fromNextSlideHandler || playerService.isScoringMode()) {
      return;
    }
    const slideId = lessonManager.currentLessonElementId;
    if (!slideId) {
      return;
    }
    const childModel = lessonManager.getSlideModel(slideId);
    if (!childModel?.isActivityPart) {
      return;
    }
    const parentModel = lessonManager.getSlideModel(childModel.parentActivityElementId);
    if (parentModel?.type !== ContentType.PDF_ACTIVITY.type) {
      return;
    }
    if (getContentChildIdsForPdfPage(parentModel, 1).length > 0) {
      return;
    }
    docReaderManager.clearPdfEntryViaActivityParentSlide();
  }

  /**
   * docReader page sync when the reader's page or lesson slide changes — navigates to the child
   * question slide linked to the displayed PDF page (passage-style split layout).
   */
  applyPdfActivityReaderSlideSync = () => {
    const blockFlyoutAfterFooterPrevious =
      docReaderManager.suppressInteractiveFlyoutOpenAfterFooterPreviousSlide;
    try {
      if (playerService.isScoringMode()) {
        return;
      }
      const slideId = lessonManager.currentLessonElementId;
      if (!slideId) {
        return;
      }
      const childModel = lessonManager.getSlideModel(slideId);
      if (!childModel?.isActivityPart) {
        return;
      }
      const parentModel = lessonManager.getSlideModel(childModel.parentActivityElementId);
      if (parentModel?.type !== ContentType.PDF_ACTIVITY.type) {
        return;
      }
      const pdfActivityLessonElementId = parentModel.lessonElementId;
      const page = docReaderManager.currentPageNumber;
      if (page == null || page < 1) {
        return;
      }

      if (pdfActivityNavigationService.tryApplyBlankPanelForPage(pdfActivityLessonElementId, page)) {
        return;
      }

      const pageChanged = pdfActivityNavigationService.readerPageChangedSinceLastSync(page);
      if (docReaderManager.pdfManualChildNav && !pageChanged) {
        return;
      }
      if (docReaderManager.pdfManualChildNav && pageChanged) {
        docReaderManager.setPdfManualChildNav(false);
        docReaderManager.setPdfDisplayChildLessonElementId(null);
      }

      const activityEl = lessonManager.getLessonElement(pdfActivityLessonElementId);
      const orderedIds = activityEl?.lessonElementIds;
      if (!orderedIds?.length) {
        return;
      }
      const filteredSlides = lessonManager.getFilteredSlides();

      let questionPageForCurrentSlide = null;
      if (childModel.parentActivityElementId === pdfActivityLessonElementId) {
        questionPageForCurrentSlide = getPageForActivityChild(parentModel, childModel);
      }

      const pendingProgrammaticNav = docReaderManager.isExpectingProgrammaticPageChange();
      const pendingLessonReaderPage = docReaderManager.pdfLessonPendingReaderPage;
      if (pendingLessonReaderPage != null) {
        if (Number(page) === Number(pendingLessonReaderPage)) {
          docReaderManager.clearPdfLessonPendingReaderPage();
        } else if (pendingProgrammaticNav) {
          return;
        } else {
          docReaderManager.clearPdfLessonPendingReaderPage();
        }
      }

      const pdfGotoPageOne = docReaderManager.pdfGotoOutsideActivityNavPageOne;
      const staleReaderPageOne =
      !pdfGotoPageOne &&
      pendingProgrammaticNav &&
      page === 1 &&
      questionPageForCurrentSlide != null &&
      Number(questionPageForCurrentSlide) !== 1;

      if (pendingProgrammaticNav && staleReaderPageOne) {
        return;
      }

      pdfActivityNavigationService.markReaderPageSynced(page);

      const proposedLessonElementId = getFirstLessonElementIdOnPdfPage(
        parentModel,
        page,
        orderedIds,
        (id) => lessonManager.getSlideModel(id),
        filteredSlides
      );
      if (!proposedLessonElementId) {
        return;
      }
      if (childModel.parentActivityElementId === pdfActivityLessonElementId) {
        const questionPage = questionPageForCurrentSlide;
        if (
          questionPage != null &&
          Number(questionPage) === Number(page) &&
          !pdfActivityNavigationService.shouldShowBlankPanel(pdfActivityLessonElementId, page)
        ) {
          pdfActivityNavigationService.persistState(pdfActivityLessonElementId, {
            page,
            childLessonElementId: slideId,
            manualChildNav: false,
          }).catch((err) => console.error(err));
          return;
        }
      }
      pdfActivityNavigationService.showChildQuestionPanel(pdfActivityLessonElementId, null);
      const proposedEntry = navMenuManager.activityNavigatorLessonElementMap.get(proposedLessonElementId);
      const { currentActivityNavigatorLessonElementId } = navMenuManager;
      let targetLessonElementId = proposedLessonElementId;
      if (proposedEntry?.groupList?.includes?.(currentActivityNavigatorLessonElementId)) {
        targetLessonElementId = currentActivityNavigatorLessonElementId;
      }
      pdfActivityNavigationService.persistState(pdfActivityLessonElementId, {
        page,
        childLessonElementId: targetLessonElementId,
        manualChildNav: false,
      }).catch((err) => console.error(err));
      this.navToSlideClickHandler(targetLessonElementId, {
        shouldReturnIfSameLessonElementId: false,
        fromPdfActivityReaderFlyoutSync: true,
      }).catch((err) => console.error(err));
    } finally {
      if (blockFlyoutAfterFooterPrevious) {
        setTimeout(() => {
          docReaderManager.clearSuppressInteractiveFlyoutOpenAfterFooterPreviousSlide();
        }, 0);
      }
    }
  }

  restorePdfActivityNavigation = async (parentLessonElementId) => {
    const parentModel = lessonManager.getSlideModel(parentLessonElementId);
    if (parentModel?.type !== ContentType.PDF_ACTIVITY.type) {
      return;
    }
    if (!pdfActivityNavigationService.beginRestore(parentLessonElementId)) {
      return;
    }
    try {
      const saved = pdfActivityNavigationService.getSavedNavigation(parentLessonElementId);
      const { needsChildNav, childLessonElementId } =
        pdfActivityNavigationService.applySavedNavigationUi(parentLessonElementId, saved);
      if (needsChildNav && childLessonElementId) {
        await this.navToSlideClickHandler(childLessonElementId, {
          shouldReturnIfSameLessonElementId: false,
          fromPdfActivityReaderFlyoutSync: true,
          shouldScrollQuestionIntoView: false,
        });
      } else if (!saved?.pdfLastPage && parentModel) {
        pdfActivityNavigationService.refreshBlankPanelFromReader(parentLessonElementId);
      }
    } finally {
      pdfActivityNavigationService.endRestore();
      setTimeout(() => {
        pdfActivityNavigationService.refreshBlankPanelFromReader(parentLessonElementId);
      }, 250);
    }
  }

  gotoButtonHandler = (_event, toggle) => {
    navMenuManager.toggleGotoMenuState(toggle);
  }

  scrollDownHandler = (ref) => {
    if (ref.current) {
      ref.current.scrollTop += 100;
    }
  }

  scrollUpHandler = (ref) => {
    if (ref.current) {
      ref.current.scrollTop -= 100;
    }
  }

  scrollRightHandler = (ref) => {
    if (ref.current) {
      ref.current.scrollLeft += (this.cardWidth + 22);
    }
  }

  scrollLeftHandler = (ref) => {
    if (ref.current) {
      ref.current.scrollLeft -= (this.cardWidth + 22);
    }
  }

  toggleSectionOpen = (lessonElementId) => {
    const toggle = navMenuManager.summarySectionOpenToggles.get(lessonElementId);
    if (toggle === undefined || toggle === null) {
      return;
    }
    navMenuManager.setSummarySectionOpen(lessonElementId, !toggle);
  }

  doPreNavigationActions = async (skipSave = false) => {
    const mode = lessonManager.playerMode;
    const model = lessonManager.getSlideModel(lessonManager.currentLessonElementId);
    if (model === null) { // can be null in item preview mode.
      return;
    }

    const state = responseManager.getLessonElementState(lessonManager.currentLessonElementId);

    await responseService.flushBufferedInteractiveMessage(lessonManager.currentLessonElementId);

    if (state && state.correctAnswerShowing) {
      state.questionBehavior.resetStudentAnswer(state, false);
      state.turnOffAnswerShowing();
      if (state.isSubmitted) {
        await responseManager.scoreResponse(model, lessonManager.currentLessonElementId, false);
      }
    }

    audioManager.clearAudioQue();

    // Only close resource if we are leaving the activity.
    if (!model?.isActivityPart && toolbarManager.isResourcesOpen) {
      toolbarManager.setIsResourcesOpen(false);
      toolbarManager.toggleToolOff(toolbarManager?.resourceOpenId);
    }

    const allowSave = !skipSave;

    if (allowSave && (mode === LessonMode.ACTIVITY_MODE || mode === LessonMode.TEACHER_PRESENTATION)) {
      toolbarService.buildToolPersistence(lessonManager.currentLessonElementId);
      if (model.isActivityPart) {
        toolbarService.buildToolPersistence(model.parentActivityElementId);
        const parentModel = lessonManager.getSlideModel(model.parentActivityElementId);
        if (parentModel?.type === ContentType.PDF_ACTIVITY.type) {
          await pdfActivityNavigationService.persistState(parentModel.lessonElementId, {
            page: docReaderManager.currentPageNumber,
            childLessonElementId: docReaderManager.pdfQuestionPanelBlank
              ? null
              : (docReaderManager.pdfDisplayChildLessonElementId || model.lessonElementId),
            manualChildNav: docReaderManager.pdfManualChildNav,
          });
        }
      }
      await timeManager.stopLessonElementTimer(lessonManager.currentLessonElementId);
      await responseService.saveSingleResponse(lessonManager.currentLessonElementId);
    } else if ((mode === LessonMode.PREVIEW || mode === LessonMode.ITEM_PREVIEW || mode === LessonMode.PUBLISHER_PREVIEW) && model.unscored && (model.type === ContentType.ALCHEMIE_TEST_ITEM.type || model.type === ContentType.STAND_ALONE_ALCHEMIE_ITEM.type)) {
      responseManager.setCurrentResponseSaved(lessonManager.currentLessonElementId, false, { locked: false, text: '' });
    }

    if (lessonManager.playerMode === LessonMode.SCORING) {
      await scoringService.updateTestItemScore(lessonManager.currentLessonElementId);
      await scoringService.submitTeacherComment(lessonManager.currentLessonElementId);
    }

    if (pdfActivityNavigationService.isPreviewLikeMode() &&
      state && state.questionBehavior && state.currentResponseAnswer) {
      const answered = state.questionBehavior.checkForValidResponse(state.currentResponseAnswer, model);
      const submitButtonVisible = !!questionFeedbackManager.getUiState(lessonManager.currentLessonElementId)?.submitButtonVisible;

      if (answered && model.isTestItem && !submitButtonVisible && !state.questionBehavior.requiresExplicitSubmitOnNavigation()) {
        responseManager.setPreviewResponseSubmitted(lessonManager.currentLessonElementId, true);
      }
      if (model.isActivityPart) {
        const parentModel = lessonManager.getSlideModel(model.parentActivityElementId);
        if (parentModel?.type === ContentType.PDF_ACTIVITY.type) {
          await pdfActivityNavigationService.persistState(parentModel.lessonElementId, {
            page: docReaderManager.currentPageNumber,
            childLessonElementId: docReaderManager.pdfQuestionPanelBlank
              ? null
              : (docReaderManager.pdfDisplayChildLessonElementId || model.lessonElementId),
            manualChildNav: docReaderManager.pdfManualChildNav,
          });
        }
      }
    }

    if (toolbarManager.isStickyNotesOpen) {
      await highlightAnnotationService.cancelCreateNote();
    }

    // If reveal is open we want to it to reset when we navigate to cover the page so we set it to false and then true to have it rerender.
    if (toolbarManager.isRevealOpen) {
      toolbarManager.setIsRevealOpen(false);
      toolbarManager.setIsRevealOpen(true);
    }
  }

  toggleInteractiveFlyout = () => {
    if (lessonManager.isInteractiveFlyoutOpen) {
      lessonManager.setIsInteractiveFlyoutOpen(false);
    } else {
      lessonManager.setIsInteractiveFlyoutOpen(true);
    }
  }

  setInteractiveFlyoutOpen = (val) => {
    lessonManager.setIsInteractiveFlyoutOpen(val);
  }

  async doPostNavigationActions({
    event,
    shouldScrollQuestionIntoView = true,
    fromNextSlideHandler = false,
    fromPreviousSlideHandler = false,
    fromCardHandler = false,
    fromGotoMenuCard = false,
  } = {}) {
    const lessonElementId = lessonManager.currentLessonElementId;
    const model = lessonManager.getSlideModel(lessonElementId);
    if (model === null) { // can be null in item_preview mode
      return;
    }
    const { isActivityPart } = model;
    const parentLessonElementId = (isActivityPart) ? model.parentActivityElementId : null;
    if (lessonManager.isInteractiveViewer) {
      const { interactiveElementId } = lessonManager;

      if (parentLessonElementId !== interactiveElementId) {
        lessonManager.setIsInteractiveFlyoutOpen(false);
        lessonManager.setInteractiveElementId(parentLessonElementId);
      } else if (
        docReaderManager.suppressInteractiveFlyoutOpenAfterFooterPreviousSlide ||
        (fromGotoMenuCard && !lessonManager.lessonOptions.navigateWithinActivity)
      ) {
        lessonManager.setIsInteractiveFlyoutOpen(false);
      } else {
        lessonManager.setIsInteractiveFlyoutOpen(true);
      }
    }

    questionFeedbackManager.setHintDialogOpen(false);
    questionFeedbackManager.setSolutionDialogOpen(false);
    htmlService.closeMathTypeModal();

    // Only close resource if we are leaving the activity.
    if (!model?.isActivityPart && toolbarManager.isResourcesOpen) {
      toolbarManager.setIsResourcesOpen(false);
      toolbarManager.toggleToolOff(toolbarManager?.resourceOpenId);
    }

    if (shouldScrollQuestionIntoView) {
      questionService.scrollQuestionIntoView(lessonElementId, {
        shouldScrollQuestionIntoView,
        fromNextSlideHandler,
        fromPreviousSlideHandler,
        fromCardHandler
      });
    }

    const lessonElement = lessonManager.getLessonElement(lessonManager.currentLessonElementId);
    const lessonElementState = responseManager.getLessonElementState(lessonManager.currentLessonElementId);

    responseService.loadSlide(lessonElementId, lessonManager.playerMode);

    if (this.shouldGetLessonAlignments() && lessonManager.lessonContentItem) {
      await alignmentService.fetchServerLessonAlignmentData(lessonManager.lessonContentItem.id,
        lessonManager.courseContentItemId, studentActivityManager.activityId);

      if (model && model.isActivityPart) {
        await alignmentService.fetchContentItemAlignments(model.parentActivityElementId,
          lessonManager.courseContentItemId, studentActivityManager.activityId, lessonElement.entityId);
      }

      if (lessonElement && model && studentActivityManager && lessonManager) {
        await alignmentService.fetchContentItemAlignments(lessonElement.id,
          lessonManager.courseContentItemId, studentActivityManager.activityId, lessonElement.entityId);
      }
    }

    let gotRubric = false;

    if (this.shouldGetRubric() &&
      model && model.rubricEntityId) {
      if (!scoringManager.rubricMap.has(lessonElement.id)) {
        gotRubric = await scoringService.fetchContentItemRubric(lessonElement.id, model);
      } else {
        gotRubric = true;
      }

      if (gotRubric) {
        const lessonElementState = responseManager.getLessonElementState(lessonElement.id);
        if (lessonElementState && lessonElementState.subscore) {
          scoringManager.setSubscore(lessonElement.id, JSON.parse(lessonElementState.subscore), lessonElementState.isScored);

          // For AI-scored items, recalculate total from category scores to ensure consistency
          // This is needed because AI scoring stores the overallScore separately from rubricCriteria scores,
          // and they may not always match. Manual scoring always calculates the total from categories.
          if (lessonElementState.scoreState === ScoreState.AI_SCORED ||
              lessonElementState.scoreState === ScoreState.AI_OVERRIDDEN) {
            scoringManager.calculateNewScoreFromRubricTable(lessonElement.id);
            // Update submittedItemScore to match the recalculated total. This prevents updateTestItemScore
            // from incorrectly detecting a change (when the teacher has not actually modified anything),
            // which would trigger an unnecessary backend call that marks the question as AI_OVERRIDDEN.
            const recalculatedScore = scoringManager.getTempScore(lessonElement.id);
            lessonElementState.setSubmittedItemScore(recalculatedScore);
          }
        }
      }
    }

    if (lessonManager.playerMode === LessonMode.ACTIVITY_MODE || lessonManager.playerMode === LessonMode.TEACHER_PRESENTATION) {
      if (lessonManager.currentLessonElementId !== LessonSlideIds.SUMMARY_PAGE && lessonManager.currentLessonElementId !== LessonSlideIds.TITLE_PAGE) {
        await studentActivityService.saveLastViewed(lessonManager.currentLessonElementId);
        timeManager.startLessonElementTimer(lessonManager.currentLessonElementId);
      }
    }

    toolbarService.toggleTools();

    if (lessonManager.playerMode === LessonMode.ACTIVITY_MODE || lessonManager.playerMode === LessonMode.TEACHER_PRESENTATION || lessonManager.playerMode === LessonMode.SCORING) {
      toolbarService.loadToolPersistence(lessonManager.currentLessonElementId, lessonElementState);
      if (model.isActivityPart) {
        const parentState = responseManager.getLessonElementState(model.parentActivityElementId);
        toolbarService.loadToolPersistence(model.parentActivityElementId, parentState.toolPersistence);
      }
    }
    if (lessonManager.playerMode === LessonMode.PREVIEW || lessonManager.playerMode === LessonMode.PUBLISHER_PREVIEW) {
      if (lessonElementState && lessonElementState.isSurvey && !lessonElementState.isUnScored) {
        responseManager.addNotScoredList(lessonElementId);
      }
    }

    // Only show highlights/notes on navigation if the user has the tool toggled on.
    // This ensures "init ON" behavior doesn't override a user's choice to toggle off.
    if (toolbarManager.isHighlightAnnotationOpen || toolbarManager.isToolOn('StickyNotes')) {
      const highlightableElements = document.querySelectorAll('.html-input, .paragraph-wrapper');
      const stickyNotesAvailable = toolbarManager.contentToolAvailability?.includes?.('StickyNotes');
      for (const highlightableElement of highlightableElements) {
        if (stickyNotesAvailable) {
          highlightableElement.classList.add('sticky-notes-active');
        } else {
          highlightableElement.classList.remove('sticky-notes-active');
        }
      }
      if (model.isActivityPart) {
        const parentLessonElement = lessonManager.getLessonElement(model.parentActivityElementId);
        if (parentLessonElement && parentLessonElement.lessonElementIds) {
          await highlightAnnotationService.showHighlights(parentLessonElement?.id);
          for (const childLessonIds of parentLessonElement.lessonElementIds) {
            await highlightAnnotationService.showHighlights(childLessonIds);
          }
        }
      } else {
        // reset the current dom element as we have changed context
        highlightAnnotationManager.setCurrentDomElementId(null);
        await highlightAnnotationService.showHighlights(lessonManager.currentLessonElementId);
      }
    }
  }

  doNavigationForPrintPreview = async (id) => {
    const model = lessonManager.getSlideModel(id);
    if (!model.isActivity) {
      responseService.loadSlide(id, LessonMode.PREVIEW);
    }
  }

  shouldGetTeacherComments = () => {
    const mode = lessonManager.playerMode;
    if ((mode === LessonMode.SCORING || mode === LessonMode.REVIEW) &&
    (lessonManager.currentLessonElementId !== LessonSlideIds.TITLE_PAGE &&
        lessonManager.currentLessonElementId !== LessonSlideIds.SUMMARY_PAGE)) {
      return true;
    }
    return false;
  }

  shouldGetRubric = () => {
    const mode = lessonManager.playerMode;
    if ((mode === LessonMode.SCORING || mode === LessonMode.REVIEW ||
      ((mode === LessonMode.PREVIEW || mode === LessonMode.ITEM_PREVIEW || mode === LessonMode.PUBLISHER_PREVIEW) && userManager.canViewAsTeacher)) &&
      (lessonManager.currentLessonElementId !== LessonSlideIds.TITLE_PAGE &&
      lessonManager.currentLessonElementId !== LessonSlideIds.SUMMARY_PAGE)) {
      return true;
    }
    return false;
  }

  shouldGetLessonAlignments = () => {
    return (lessonManager.playerMode === LessonMode.REVIEW ||
        lessonManager.playerMode === LessonMode.PREVIEW ||
        lessonManager.playerMode === LessonMode.PUBLISHER_PREVIEW ||
        lessonManager.playerMode === LessonMode.SCORING ||
        lessonManager.playerMode === LessonMode.PRINT_PREVIEW) &&
      lessonManager.currentLessonElementId !== LessonSlideIds.TITLE_PAGE &&
      lessonManager.currentLessonElementId !== LessonSlideIds.SUMMARY_PAGE;
  }

  initFlyoutOpenState = () => {
    const mode = lessonManager.playerMode;
    if (!lessonManager.structureMap) {
      return;
    }
    const lessonElementIds = Object.keys(lessonManager.structureMap);

    lessonElementIds.forEach((lessonElementId) => {
      const model = lessonManager.getSlideModel(lessonElementId);
      if (model && !navMenuManager.flyoutOpenStates.has(lessonElementId)) {
        const value = (mode === LessonMode.SCORING || mode === LessonMode.REVIEW) ?
          { open: FlyoutOpenState.OPEN, tab: FlyoutTabs.SCORE, side: FlyoutSide.RIGHT } :
          { open: FlyoutOpenState.CLOSED, tab: FlyoutTabs.NONE, side: (model?.isActivity) ? FlyoutSide.LEFT : FlyoutSide.RIGHT };
        navMenuManager.setFlyoutOpenState(value, lessonElementId);
      }
    });
  }

  initFlyupOpenState = (value, lessonElementId) => {
    if (navMenuManager.flyupOpenStates.size === 0 || !navMenuManager.flyupOpenStates.has(lessonElementId)) {
      navMenuManager.setFlyupOpenState(value, lessonElementId);
    }
  }

  toggleFlyupPanel = (lessonElementId) => {
    const state = navMenuManager.getFlyupOpenState(lessonElementId);
    if (state === FlyoutOpenState.OPEN) {
      navMenuManager.setFlyupOpenState(FlyoutOpenState.CLOSED, lessonElementId);
    } else {
      navMenuManager.setFlyupOpenState(FlyoutOpenState.OPEN, lessonElementId);
    }
  }

  toggleFlyoutPanel = (closeFirst, openState, lessonElementId) => {
    const os = navMenuManager.getFlyoutOpenState(lessonElementId);
    if (closeFirst && (os.open === FlyoutOpenState.OPEN || os.open === FlyoutOpenState.OPEN_WIDE || os.open === FlyoutOpenState.OPEN_WIDER)) {
      navMenuManager.setFlyoutOpenState({ open: FlyoutOpenState.CLOSED, tab: os.tab, side: os.side }, lessonElementId);
      return true; // we closed the panel
    } else {
      navMenuManager.setFlyoutOpenState({ open: openState, tab: os.tab, side: os.side }, lessonElementId);
      return false; // panel is open
    }
  }

  setFlyoutPanelTab = (tab, lessonElementId) => {
    const os = navMenuManager.getFlyoutOpenState(lessonElementId);
    navMenuManager.setFlyoutOpenState({ open: os.open, tab, side: os.side }, lessonElementId);
  }

  setCardWidths = () => {
    const buttonWidth = parseInt(getComputedStyle(document.documentElement)
      .getPropertyValue('--goto-nav-scroll-width'));

    const rootElement = document.documentElement;
    const width = rootElement.clientWidth;

    this.cardWidth = width - (buttonWidth * 2);
  }
}
export default new NavMenuService();
